import React, { Component } from "react";
import { withCookies, Cookies } from "react-cookie";
import { instanceOf } from "prop-types";
import { withRouter } from "react-router-dom";
// Services
import paymentsService from '@services/payments';
import providersService from '@services/providers';

// Components
import Menu from "@components/Menu";
import ReactTable from "@components/ReactTable";
import DateRangePicker from "@components/DateRangePicker";
import AutoComplete from "@components/AutoComplete";

import Print from './print';
import PDFPrint from '@components/PdfPrint';
import DocumentPrintModal from '@components/DocumentPrintModal';
import { EventBus as eventBus } from "@components/EventBus";
import { hideGlobalLoading } from "@services/axiosInstance";

import { 
  MdAddCircleOutline,
  MdAttachMoney,
  MdDescription,
  MdCheckBox,
  MdVisibility,
  MdPrint
} from "react-icons/md";

import { TimestampToDate } from "@utils/date";
import { twoDecimal } from "@utils/format";

import UserContext from "@components/context/userContext";

import "./list.css";

class Page extends Component {
  static contextType = UserContext;

  static propTypes = {
    cookies: instanceOf(Cookies).isRequired,
  };

  constructor(props) {
    super(props);

    this.state = {
      loaded: false,
      postos: [],
      tpvs: [],
      providers: [],
      statistics: {
        totalPayments: 0,
        totalJSCM: 0,
        totalProviders: 0,
        totalValue: 0,
        loading: true
      },
      table: {
        columns: [
          {
            Header: "Tipo",
            id: "tipo",
            accessor: (d) => d.tipo === 'recibo_fornecedor' ? 'Recibo' : 'JSCM',
          },
          {
            Header: "Registo",
            id: "registo",
            accessor: (d) => {
              if (d.tipo === 'recibo_fornecedor') {
                return `REC-${d.numero}`;
              }
              return `${d.posto}${d.tpv}${d.serie || ''}/${d.numero || ''}`;
            },
          },
          {
            Header: "Data",
            id: "data",
            accessor: "data",
          },
          {
            Header: "Hora",
            id: "hora",
            accessor: (d) => {
              return d.hora;
            },
          },
          {
            Header: "Fornecedor",
            id: "fornecedor",
            accessor: (d) => {
              if (d.tipo === 'recibo_fornecedor') {
                return d.nome_1 || d.provider_name || '-';
              }
              return '-';
            },
          },
          {
            Header: "Posto",
            id: "posto",
            accessor: (d) => {
              if (d.tipo === 'recibo_fornecedor') {
                return '-';
              }
              return d.posto || '-';
            },
          },
          {
            Header: "TPV",
            id: "tpv",
            accessor: (d) => {
              if (d.tipo === 'recibo_fornecedor') return '-';
              return d.tpv || '-';
            },
          },
          {
            Header: "Turno",
            id: "turno",
            accessor: (d) => {
              if (d.tipo === 'recibo_fornecedor') return '-';
              return d.turno || '-';
            },
          },
          {
            Header: "Valor",
            id: "valor",
            accessor: (d) => {
              const valor = d.valor || d.valor_liquido || 0;
              return twoDecimal(valor);
            },
          },
          {
            Header: "Código Raspadinha",
            id: "cod_raspadinha",
            accessor: (d) => {
              if (d.tipo === 'recibo_fornecedor') return '-';
              return d.cod_raspadinha || '-';
            },
          },
          {
            Header: "Nome Raspadinha",
            id: "nome_raspadinha",
            accessor: (d) => {
              return d.nome_raspadinha || '-';
            },
          },
          {
            Header: "Observações",
            id: "observacoes",
            accessor: (d) => {
              return d.obs || '-';
            },
          },
          {
            Header: "Ações",
            id: "acoes",
            accessor: "acoes",
            minWidth: 60,
            maxWidth: 70,
            width: 65,
          },
        ],
        rows: [],
        loading: true,
      },
      tipo: JSON.parse(localStorage.getItem("pagamentos.tipo")) || {
        value: "",
        label: "TODOS",
      },
      posto: JSON.parse(localStorage.getItem("pagamentos.posto")) || {
        value: "",
        label: "TODOS",
      },
      tpv: JSON.parse(localStorage.getItem("pagamentos.tpv")) || {
        value: "",
        label: "TODOS",
      },
      provider: JSON.parse(localStorage.getItem("pagamentos.provider")) || {
        value: "",
        label: "TODOS",
      },
      date: [(new Date()).setHours(0, 0, 0), (new Date()).setHours(23, 59, 59)],
      previewUrl: null,
      printingRow: null,
    };
    this.loadFunction = this.loadFunction.bind(this);
    this.localDateChange = this.localDateChange.bind(this);
    this.handleChange = this.handleChange.bind(this);
    this.openRowPrintModal = this.openRowPrintModal.bind(this);
    this.handleRowPrint = this.handleRowPrint.bind(this);
    this.printerRef = React.createRef();
  }

  async loadFunction() {
    if (!this.state.loaded) {
      const { date } = this.state;
      const profile = this.context;

      try {
        // Carregar todos os fornecedores da empresa
        const allProviders = await providersService.get(profile.idEmpresa);
        const providersList = Array.isArray(allProviders) 
          ? allProviders.map(p => ({
              value: String(p.idprovider),
              label: p.nome_1 || p.name || `Fornecedor ${p.idprovider}`
            }))
          : [];

        // Carregar pagamentos (inclui tanto JSCM como recibos de fornecedores)
        const allPagamentos = await paymentsService.get(
          profile.idEmpresa,
          Math.floor(date[0] / 1000),
          Math.floor(date[1] / 1000)
        );

        const pagamentosArray = Array.isArray(allPagamentos) ? allPagamentos : [];
        const postos = [];
        const tpvs = [];

        // Separar pagamentos JSCM de recibos de fornecedores
        const enrichedPayments = [];
        const enrichedProviderPayments = [];

        pagamentosArray.forEach((pagamento) => {
          if (pagamento.tipo === 'recibo_fornecedor') {
            // Processar recibo de fornecedor
            // (Os fornecedores já foram carregados de providersService acima)
            // date_time já vem em segundos, não multiplicar por 1000 (TimestampToDate já faz isso)
            const dateTime = typeof pagamento.date_time === 'string' ? parseInt(pagamento.date_time, 10) : pagamento.date_time;
            const date1 = TimestampToDate(dateTime);
            enrichedProviderPayments.push({
              ...pagamento,
              tipo: 'recibo_fornecedor',
              data: date1.split(" ")[0],
              hora: date1.split(" ")[1],
              date: date1,
              valor: pagamento.valor_liquido || pagamento.valor || 0,
              fornecedor: pagamento.fornecedor,
              numero: pagamento.numero,
              acoes: (
                <>
                  <MdPrint
                    title="Imprimir"
                    style={{ fontSize: "15px", cursor: "pointer", marginRight: '8px' }}
                    onClick={() => this.openRowPrintModal({ numero: pagamento.numero })}
                  />
                  <MdVisibility
                    title="Ver"
                    style={{ fontSize: "15px", cursor: "pointer" }}
                    onClick={() => this.props.history.push(`/payments/view/${pagamento.numero}`)}
                  />
                </>
              ),
            });
          } else {
            // Processar pagamento JSCM
            // Apenas adicionar posto se tiver valor válido
            if (pagamento.posto && !postos.find((posto) => posto.value === pagamento.posto)) {
              postos.push({ value: pagamento.posto, label: pagamento.posto_name || pagamento.posto });
            }
            // Apenas adicionar tpv se tiver valor válido
            if (pagamento.tpv && !tpvs.find((tpv) => tpv.value === pagamento.tpv)) {
              tpvs.push({ value: pagamento.tpv, label: pagamento.tpv });
            }
            const date1 = TimestampToDate(pagamento.date_time);
            enrichedPayments.push({
              ...pagamento,
              tipo: 'JSCM',
              data: date1.split(" ")[0],
              hora: date1.split(" ")[1],
              date: date1,
              valor: pagamento.valor,
              acoes: '-',
            });
          }
        });

        // Combinar ambos os tipos
        const allPayments = [...enrichedPayments, ...enrichedProviderPayments];

        // Ordenar por data (mais recente primeiro)
        // Usar date_time (timestamp) para ordenação correta
        allPayments.sort((a, b) => {
          const timestampA = a.date_time ? (typeof a.date_time === 'string' ? parseInt(a.date_time, 10) : a.date_time) : 0;
          const timestampB = b.date_time ? (typeof b.date_time === 'string' ? parseInt(b.date_time, 10) : b.date_time) : 0;
          return timestampB - timestampA; // Mais recente primeiro (maior timestamp)
        });

        // Calcular estatísticas
        const stats = {
          totalPayments: allPayments.length,
          totalJSCM: enrichedPayments.length,
          totalProviders: enrichedProviderPayments.length,
          totalValue: allPayments.reduce((sum, p) => sum + parseFloat(p.valor || p.valor_liquido || 0), 0),
          loading: false
        };

        this.setState({
          postos: [
            { value: "", label: "TODOS" },
            ...postos,
          ],
          tpvs: [
            { value: "", label: "TODOS" },
            ...tpvs,
          ],
          providers: [
            { value: "", label: "TODOS" },
            ...providersList,
          ],
          statistics: stats,
          table: {
            ...this.state.table,
            rows: allPayments,
            loading: false,
          },
          loaded: true,
        });
      } catch (error) {
        console.error('Erro ao carregar pagamentos:', error);
      }
    }
  }

  handleChange(e, callback) {
    let value = e.target.value;
    let name = e.target.name;

    this.setState({
      [name]: value,
    }, () => {
      switch (name) {
        case "posto":
        case "tpv":
        case "tipo":
        case "provider":
          localStorage.setItem(`pagamentos.${name}`, JSON.stringify(value));
          break;
        default:
          break;
      }
    });
  }

  localDateChange(date) {
    this.setState({
      date: [
        date[0].setHours(0, 0, 0),
        date[1].setHours(23, 59, 59),
      ],
      loaded: false,
      table: {
        ...this.state.table,
        loading: true,
      }
    }, () => {
      this.loadFunction();
    });
  }

  openRowPrintModal(row) {
    this.setState({ printingRow: row }, () => {
      setTimeout(() => {
        eventBus.$emit("Modal-document-print", { header: null, body: null, isOpen: true });
      }, 10);
    });
  }

  async handleRowPrint({ selectedPageSize }) {
    const profile = this.context;
    const idempresa = profile.idEmpresa;
    const numero = this.state.printingRow?.numero;
    if (!idempresa || !numero) return;
    try {
      const url = paymentsService.getPrintUrl(idempresa, numero, selectedPageSize);
      await this.setState({ previewUrl: url });
      if (this.printerRef?.current && typeof this.printerRef.current.handlePrint === 'function') {
        await this.printerRef.current.handlePrint();
      }
    } finally {
      hideGlobalLoading();
    }
  }

  render() {
    const { table, postos, posto, tpvs, tpv, providers, provider, tipo, date, statistics } = this.state;
    const { location, history } = this.props;
    const cookies = new URLSearchParams(location.search);

    const tipoOptions = [
      { value: "", label: "TODOS" },
      { value: "JSCM", label: "JSCM" },
      { value: "recibo_fornecedor", label: "Recibo" },
    ];

    const filteredTable = {
      ...table,
      rows: table.rows
        .filter((pagamento) => {
          if (tipo.value && pagamento.tipo !== tipo.value) return false;
          if (tipo.value === "JSCM" || !tipo.value) {
            if (posto.value && pagamento.posto !== posto.value) return false;
            if (tpv.value && pagamento.tpv !== tpv.value) return false;
          }
          if (tipo.value === "recibo_fornecedor" || !tipo.value) {
            if (provider.value) {
              const providerValue = String(provider.value);
              const pagamentoFornecedor = String(pagamento.fornecedor || '');
              if (pagamentoFornecedor !== providerValue) return false;
            }
          }
          return true;
        })
    };

    // Verificar se há recibos de fornecedores nos dados originais (antes do filtro)
    const hasProviderPayments = table.rows.some(p => p.tipo === 'recibo_fornecedor');
    // Verificar se há JSCM nos dados originais (antes do filtro)
    const hasJSCMPayments = table.rows.some(p => p.tipo === 'JSCM');

    // Lógica de visibilidade:
    // Se não há fornecedores nos dados originais OU tipo JSCM selecionado -> esconder filtro/colunas de fornecedores
    const hideProviderFields = !hasProviderPayments || tipo.value === "JSCM";
    // Se não há JSCM nos dados originais OU tipo recibo_fornecedor selecionado -> esconder filtros/colunas de JSCM
    const hideJSCMFields = !hasJSCMPayments || tipo.value === "recibo_fornecedor";

    const totalValue = filteredTable.rows.reduce((acc, row) => {
      const valor = parseFloat(row.valor || row.valor_liquido || 0);
      return acc + valor;
    }, 0);

    // Calcular raspadinha mais alta e média (apenas para JSCM)
    const raspadinhinhas = filteredTable.rows
      .filter(row => row.tipo === 'JSCM' && row.valor)
      .map(row => parseFloat(row.valor || 0));
    const highestRaspadinha = raspadinhinhas.length > 0 ? Math.max(...raspadinhinhas) : 0;
    const averageRaspadinha = raspadinhinhas.length > 0 
      ? raspadinhinhas.reduce((sum, val) => sum + val, 0) / raspadinhinhas.length 
      : 0;

    return (
      <div>
        <Menu
          loadFunction={this.loadFunction}
          location={location}
          history={history}
          newCookies={cookies}
        />
        <div className="fullContainer" style={{ maxWidth: "100%" }}>
          <div className="col-xs-12">
            <ol
              style={{
                textAlign: "left",
                marginTop: "75px",
              }}
              className="breadcrumb"
            >
              <li>
                <a href="http://eposgestofinal.com/index.php">Portal</a>
              </li>
              <li className="active">Pagamentos</li>
            </ol>
          </div>
        </div>

        {/* Seção de Estatísticas */}
        <div className="stats-section">
          <div className="stats-section-header">
            <h3 className="stats-title">
              Estatísticas de Pagamentos
            </h3>
          </div>
          <div className="stats-cards-container">
            {/* Card 1: Total de Pagamentos */}
            <div className="stat-card total">
              <div className="stat-card-header">
                <div className="stat-card-icon">
                  <MdDescription />
                </div>
                <div className="stat-card-content">
                  <div className="stat-value">
                    {statistics.loading ? (
                      <span>-</span>
                    ) : (
                      statistics.totalPayments.toLocaleString()
                    )}
                  </div>
                  <p className="stat-label">Total Pagamentos</p>
                </div>
              </div>
            </div>

            {/* Card 2: JSCM */}
            <div className="stat-card types">
              <div className="stat-card-header">
                <div className="stat-card-icon">
                  <MdCheckBox />
                </div>
                <div className="stat-card-content">
                  <div className="stat-value">
                    {statistics.loading ? (
                      <span>-</span>
                    ) : (
                      statistics.totalJSCM.toLocaleString()
                    )}
                  </div>
                  <p className="stat-label">Raspadinhas</p>
                  {highestRaspadinha > 0 && (
                    <div style={{ marginTop: '0.5rem', display: 'flex', gap: '1rem', fontSize: '0.875rem', color: '#6b7280' }}>
                      <div className="stat-sub-value">
                        Mais Alta: {twoDecimal(highestRaspadinha)}€
                      </div>
                      <div className="stat-sub-value">
                        Média: {twoDecimal(averageRaspadinha)}€
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Card 3: Fornecedores */}
            <div className="stat-card types">
              <div className="stat-card-header">
                <div className="stat-card-icon">
                  <MdCheckBox />
                </div>
                <div className="stat-card-content">
                  <div className="stat-value">
                    {statistics.loading ? (
                      <span>-</span>
                    ) : (
                      statistics.totalProviders.toLocaleString()
                    )}
                  </div>
                  <p className="stat-label">Recibo</p>
                </div>
              </div>
            </div>

            {/* Card 4: Valor Total */}
            <div className="stat-card value">
              <div className="stat-card-header">
                <div className="stat-card-icon">
                  <MdAttachMoney />
                </div>
                <div className="stat-card-content">
                  <div className="stat-value">
                    {statistics.loading ? (
                      <span>-</span>
                    ) : (
                      `${twoDecimal(statistics.totalValue)}€`
                    )}
                  </div>
                  <p className="stat-label">Valor Total</p>
                </div>
              </div>
            </div>

          </div>
        </div>

        {/* Seção de Filtros */}
        <div className="filters-section">
          <div className="filters-header">
            <h3 className="filters-title">Filtros de Pagamentos</h3>
            <button 
              className="create-payment-btn"
              onClick={() => history.push("/payments/new")}
            >
              <MdAddCircleOutline />
              <span>Criar Pagamento</span>
            </button>
          </div>
          
          <div className="filters-container">
            <div className="filter-group">
              <label className="filter-label">Tipo</label>
              <AutoComplete
                name="tipo"
                value={tipo}
                handleChange={this.handleChange}
                options={tipoOptions}
              />
            </div>
            
            <div className="filter-group" style={{ minWidth: '290px' }}>
              <label className="filter-label">Data</label>
              <div className="date-filter-container">
                <DateRangePicker
                  maxDate={new Date()}
                  value={date}
                  onChange={this.localDateChange}
                />
              </div>
            </div>
            
            {!hideJSCMFields && (
              <>
                <div className="filter-group">
                  <label className="filter-label">Posto</label>
                  <AutoComplete
                    name="posto"
                    value={posto}
                    handleChange={this.handleChange}
                    options={postos}
                  />
                </div>
                <div className="filter-group">
                  <label className="filter-label">TPV</label>
                  <AutoComplete
                    name="tpv"
                    value={tpv}
                    handleChange={this.handleChange}
                    options={tpvs}
                  />
                </div>
              </>
            )}
            
            {!hideProviderFields && (
              <div className="filter-group">
                <label className="filter-label">Fornecedor</label>
                <AutoComplete
                  name="provider"
                  value={provider}
                  handleChange={this.handleChange}
                  options={providers}
                />
              </div>
            )}
          </div>
        </div>

        {/* Total */}
        <div className="total-section">
          <div className="total-label">Total Filtrado:</div>
          <div className="total-value">{twoDecimal(totalValue)}€</div>
        </div>

        {/* Tabela */}
        <div className="table-container">
          <div className="table-wrapper">
            <div className="table-actions">
              <Print
                table={filteredTable}
                date={date}
                posto={posto}
                tpv={tpv}
                tipo={tipo}
                provider={provider}
              />
            </div>
            <ReactTable
              columns={filteredTable.columns
                .filter(col => {
                  // Nunca esconder a coluna de Ações
                  if (col.id === 'acoes') {
                    return true;
                  }
                  // Esconder colunas baseado na lógica de visibilidade
                  if (hideProviderFields) {
                    // Esconder: Tipo, Fornecedor, Observações
                    if (['tipo', 'fornecedor', 'observacoes'].includes(col.id)) {
                      return false;
                    }
                  }
                  if (hideJSCMFields) {
                    // Esconder: Tipo, Posto, TPV, Turno, Código Raspadinha, Nome Raspadinha
                    if (['tipo', 'posto', 'tpv', 'turno', 'cod_raspadinha', 'nome_raspadinha'].includes(col.id)) {
                      return false;
                    }
                  }
                  return true;
                })
                .map(col => {
                // Adicionar formatação para a coluna Tipo
                if (col.id === "tipo") {
                  return {
                    ...col,
                    Cell: ({ value }) => (
                      <span
                        className={`payment-type-badge ${
                          value === "Recibo"
                            ? "payment-type-badge-provider"
                            : "payment-type-badge-jscm"
                        }`}
                      >
                        {value}
                      </span>
                    ),
                  };
                }
                return col;
              })}
              sortable={true}
              loading={filteredTable.loading}
              data={filteredTable.rows}
              excelDownload={true}
              excelTotal="valor"
            />
          </div>
        </div>
        <PDFPrint ref={this.printerRef} pdfUrl={this.state.previewUrl || ''} />
        <DocumentPrintModal
          onPrint={(args) => this.handleRowPrint(args)}
          showPageTypes={false}
          defaultPageSize="a4"
          customTitle="Imprimir Recibo"
        />
      </div>
    );
  }
}

export default withCookies(withRouter(Page));
